import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import { requerimientoService } from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/datatable.css';

const ESTADO_COLORS = { PENDIENTE: '#e09a3c', APROBADO: '#4caf7d', RECHAZADO: '#e05c5c' };

const fmt = (dt) => dt ? new Date(dt).toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' }) : '--';

const AprobacionRQ = () => {
  const [records, setRecords]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('PENDIENTE');

  const [alert, setAlert]     = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast]     = useState(null);

  // Modal de aprobación / rechazo
  const [modal, setModal]             = useState(null); // { registro, accion: 'APROBADO'|'RECHAZADO' }
  const [observacion, setObservacion] = useState('');
  const [saving, setSaving]           = useState(false);

  // Modal de detalle (solo lectura)
  const [detalle, setDetalle]         = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await requerimientoService.getAll();
      setRecords(res.data);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar requerimientos.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const registrosFiltrados = filtroEstado === 'TODOS'
    ? records
    : records.filter(r => r.estado === filtroEstado);

  // Contadores para las pestañas
  const counts = {
    PENDIENTE: records.filter(r => r.estado === 'PENDIENTE').length,
    APROBADO:  records.filter(r => r.estado === 'APROBADO').length,
    RECHAZADO: records.filter(r => r.estado === 'RECHAZADO').length,
    TODOS:     records.length,
  };

  const openModal = (registro, accion) => {
    setModal({ registro, accion });
    setObservacion('');
  };

  const handleAprobar = async () => {
    if (!modal) return;
    setSaving(true);
    try {
      await requerimientoService.aprobar(modal.registro.id_requerimiento, {
        estado: modal.accion,
        observacion_aprobacion: observacion || null,
      });
      setToast(`Requerimiento ${modal.accion === 'APROBADO' ? 'aprobado' : 'rechazado'} correctamente`);
      setModal(null);
      fetchRecords();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al procesar.') });
    } finally {
      setSaving(false);
    }
  };

  const openDetalle = async (r) => {
    setLoadingDetalle(true);
    setDetalle({ header: r, lineas: [] });
    try {
      const res = await requerimientoService.getById(r.id_requerimiento);
      setDetalle({ header: res.data, lineas: res.data.detalle || [] });
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar detalle.' });
      setDetalle(null);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const columns = [
    { key: 'numero',              label: 'Número',       width: 160, style: { fontWeight: 700 } },
    { key: 'fecha_emision',       label: 'Fecha',        width: 100,
      render: r => r.fecha_emision?.split('T')[0] || '--' },
    { key: 'tipo_documento',      label: 'Tipo',         width: 65 },
    { key: 'sucursal_nombre',     label: 'Sucursal',     width: 130 },
    { key: 'area_descripcion',    label: 'Área',         width: 120 },
    { key: 'solicitante_nombre',  label: 'Solicitante' },
    { key: 'prioridad_descripcion', label: 'Prioridad',  width: 100 },
    { key: 'estado', label: 'Estado', width: 105,
      render: r => (
        <span style={{ color: ESTADO_COLORS[r.estado] || '#aaa', fontWeight: 600 }}>{r.estado}</span>
      )},
    { key: 'usuario_creacion',    label: 'Creado por',   width: 110 },
    { key: 'fecha_creacion',      label: 'F. Creación',  width: 140,
      render: r => fmt(r.fecha_creacion) },
    { key: 'usuario_aprobacion',  label: 'Aprobado por', width: 110,
      render: r => r.usuario_aprobacion || '--' },
    { key: 'fecha_aprobacion',    label: 'F. Aprobación', width: 140,
      render: r => fmt(r.fecha_aprobacion) },
    { key: 'acciones', label: 'Acciones', width: 175,
      render: r => r.estado === 'PENDIENTE' ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={e => { e.stopPropagation(); openModal(r, 'APROBADO'); }}
            style={{ background: '#2a6e4a', border: 'none', color: '#fff', borderRadius: 4,
              padding: '3px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            ✓ Aprobar
          </button>
          <button
            onClick={e => { e.stopPropagation(); openModal(r, 'RECHAZADO'); }}
            style={{ background: '#7a2323', border: 'none', color: '#fff', borderRadius: 4,
              padding: '3px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            ✗ Rechazar
          </button>
        </div>
      ) : (
        <span style={{ color: '#4d6080', fontSize: 12 }}>
          {r.observacion_aprobacion || '—'}
        </span>
      ),
    },
  ];

  // Estilos
  const tabStyle = (key) => ({
    padding: '7px 18px', border: 'none', borderRadius: '6px 6px 0 0', cursor: 'pointer',
    fontWeight: filtroEstado === key ? 700 : 400,
    background: filtroEstado === key ? '#1a3a6a' : 'transparent',
    color: filtroEstado === key ? '#fff' : '#7a9ac0',
    borderBottom: filtroEstado === key ? '2px solid #4d9de0' : '2px solid transparent',
    fontSize: 13, transition: 'all .15s',
  });

  return (
    <Layout title="Aprobar Requerimientos" subtitle="Requerimientos">
      <div className="page-header">
        <h2>Aprobar Requerimientos</h2>
      </div>

      {/* Pestañas de filtro */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid #1a2d45', marginBottom: 12 }}>
        {[
          { key: 'PENDIENTE', label: 'Pendientes' },
          { key: 'APROBADO',  label: 'Aprobados' },
          { key: 'RECHAZADO', label: 'Rechazados' },
          { key: 'TODOS',     label: 'Todos' },
        ].map(({ key, label }) => (
          <button key={key} style={tabStyle(key)} onClick={() => setFiltroEstado(key)}>
            {label}
            <span style={{
              marginLeft: 6, background: key === 'PENDIENTE' ? '#e09a3c'
                : key === 'APROBADO' ? '#4caf7d' : key === 'RECHAZADO' ? '#e05c5c' : '#4d6080',
              color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11,
            }}>{counts[key]}</span>
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        data={registrosFiltrados}
        idField="id_requerimiento"
        loading={loading}
        emptyMessage="No hay requerimientos"
        exportFileName="aprobacion_requerimientos"
        onRowClick={openDetalle}
      />

      {/* ── MODAL DE APROBACIÓN / RECHAZO ── */}
      {modal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ color: modal.accion === 'APROBADO' ? '#4caf7d' : '#e05c5c' }}>
                {modal.accion === 'APROBADO' ? '✓ Aprobar' : '✗ Rechazar'} Requerimiento
              </h2>
              <button className="modal-close" onClick={() => setModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#8899aa', marginBottom: 16, fontSize: 14 }}>
                <strong style={{ color: '#c8d8e8' }}>{modal.registro.numero}</strong>
                {' — '}{modal.registro.solicitante_nombre || 'Sin solicitante'}
                {' · '}{modal.registro.area_descripcion || 'Sin área'}
              </p>
              <div className="form-row">
                <label>Observación {modal.accion === 'RECHAZADO' ? '(motivo del rechazo)' : '(opcional)'}</label>
                <textarea
                  value={observacion}
                  onChange={e => setObservacion(e.target.value)}
                  rows={3}
                  placeholder={modal.accion === 'RECHAZADO'
                    ? 'Indique el motivo del rechazo...'
                    : 'Comentario de aprobación (opcional)...'}
                  style={{ width: '100%', resize: 'vertical' }}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
              <button
                className="btn btn-primary"
                style={{ background: modal.accion === 'APROBADO' ? '#2a6e4a' : '#7a2323' }}
                onClick={handleAprobar}
                disabled={saving}>
                {saving ? 'Procesando...' : (modal.accion === 'APROBADO' ? 'Confirmar Aprobación' : 'Confirmar Rechazo')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL DETALLE (solo lectura) ── */}
      {detalle && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 780, maxHeight: '85vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detalle del Requerimiento — {detalle.header.numero}</h2>
              <button className="modal-close" onClick={() => setDetalle(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {loadingDetalle ? (
                <p style={{ color: '#4d9de0', textAlign: 'center', padding: 24 }}>Cargando...</p>
              ) : (
                <>
                  {/* Info cabecera */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px 16px', marginBottom: 16 }}>
                    {[
                      ['Estado',       <span style={{ color: ESTADO_COLORS[detalle.header.estado], fontWeight: 700 }}>{detalle.header.estado}</span>],
                      ['Sucursal',     detalle.header.sucursal_nombre],
                      ['Área',         detalle.header.area_descripcion],
                      ['Solicitante',  detalle.header.solicitante_nombre],
                      ['Prioridad',    detalle.header.prioridad_descripcion],
                      ['Proveedor',    detalle.header.proveedor_nombre],
                      ['F. Emisión',   detalle.header.fecha_emision?.split('T')[0]],
                      ['F. Entrega',   detalle.header.fecha_entrega?.split('T')[0] || '--'],
                      ['Centro Costo', detalle.header.centro_costo_descripcion],
                    ].map(([label, val]) => (
                      <div key={label}>
                        <div style={{ fontSize: 10, color: '#4d9de0', fontWeight: 700, textTransform: 'uppercase' }}>{label}</div>
                        <div style={{ color: '#c8d8e8', fontSize: 13 }}>{val || '--'}</div>
                      </div>
                    ))}
                  </div>
                  {(detalle.header.observacion1 || detalle.header.observacion2) && (
                    <div style={{ background: '#0d1e35', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#8899aa' }}>
                      {detalle.header.observacion1 && <div><strong>Obs 1:</strong> {detalle.header.observacion1}</div>}
                      {detalle.header.observacion2 && <div><strong>Obs 2:</strong> {detalle.header.observacion2}</div>}
                    </div>
                  )}

                  {/* Auditoría */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '4px 16px',
                    background: '#0a1628', borderRadius: 6, padding: '8px 12px', marginBottom: 12,
                    fontSize: 12, color: '#5a7a9a' }}>
                    <div>Creado por: <strong style={{ color: '#7a9ac0' }}>{detalle.header.usuario_creacion || '--'}</strong> · {fmt(detalle.header.fecha_creacion)}</div>
                    <div>Modificado: <strong style={{ color: '#7a9ac0' }}>{detalle.header.usuario_modificacion || '--'}</strong> · {fmt(detalle.header.fecha_modificacion)}</div>
                    {detalle.header.usuario_aprobacion && (
                      <div style={{ gridColumn: '1/-1' }}>
                        {detalle.header.estado} por: <strong style={{ color: ESTADO_COLORS[detalle.header.estado] }}>{detalle.header.usuario_aprobacion}</strong> · {fmt(detalle.header.fecha_aprobacion)}
                        {detalle.header.observacion_aprobacion && <> — {detalle.header.observacion_aprobacion}</>}
                      </div>
                    )}
                  </div>

                  {/* Detalle artículos */}
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#4d9de0',
                    borderBottom: '1px solid #1e3050', paddingBottom: 4, marginBottom: 8 }}>
                    Artículos solicitados
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#0d1e35', color: '#8899aa' }}>
                        <th style={{ padding: '6px 10px', textAlign: 'left', width: 40 }}>#</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left' }}>Artículo</th>
                        <th style={{ padding: '6px 10px', textAlign: 'right', width: 90 }}>Cantidad</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left' }}>Observación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalle.lineas.length === 0 && (
                        <tr><td colSpan={4} style={{ padding: 16, textAlign: 'center', color: '#4d6080', fontStyle: 'italic' }}>Sin artículos</td></tr>
                      )}
                      {detalle.lineas.map((l, i) => (
                        <tr key={l.id_detalle} style={{ borderBottom: '1px solid #1a2d45' }}>
                          <td style={{ padding: '5px 10px', color: '#4d9de0' }}>{i + 1}</td>
                          <td style={{ padding: '5px 10px', color: '#c8d8e8' }}>
                            {l.articulo_descripcion || `ID: ${l.id_articulo}`}
                            {l.articulo_codigo && <span style={{ color: '#4d6080', fontSize: 11, marginLeft: 6 }}>({l.articulo_codigo})</span>}
                          </td>
                          <td style={{ padding: '5px 10px', textAlign: 'right', color: '#e0c87a', fontWeight: 600 }}>{l.cantidad}</td>
                          <td style={{ padding: '5px 10px', color: '#8899aa' }}>{l.observacion || '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
            <div className="modal-footer">
              {detalle.header.estado === 'PENDIENTE' && (
                <>
                  <button className="btn btn-primary"
                    style={{ background: '#2a6e4a', marginRight: 'auto' }}
                    onClick={() => { setDetalle(null); openModal(detalle.header, 'APROBADO'); }}>
                    ✓ Aprobar
                  </button>
                  <button className="btn btn-primary"
                    style={{ background: '#7a2323' }}
                    onClick={() => { setDetalle(null); openModal(detalle.header, 'RECHAZADO'); }}>
                    ✗ Rechazar
                  </button>
                </>
              )}
              <button className="btn btn-secondary" onClick={() => setDetalle(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {alert   && <Alert   type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
      {confirm && <Confirm title={confirm.title} message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {toast   && <Toast   message={toast} onClose={() => setToast(null)} />}
    </Layout>
  );
};

export default AprobacionRQ;
